import { useAsyncData, useRequestURL } from '#imports'

import type {
  ApiArticlePageContent,
  ArticlePageContent,
} from '@backmarket/http-api/src/api-specs-content/models/article-page-content'
import type {
  BasePageContentAPI,
  MappedApiPageContent,
} from '@backmarket/http-api/src/api-specs-content/models/base-page-content'
import type { ArticleCard } from '@backmarket/nuxt-layer-cms/ArticleCard/ArticleCard.types'
import { getAsyncDataKey } from '@backmarket/nuxt-module-http-v2/getAsyncDataKey'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'

import { useFetchCMSPageContentWithFallback } from '~/scopes/cms/composables/useFetchCMSPageContentWithFallback'
import { useFormatContentResponse } from '~/scopes/cms/composables/useFormatContentResponse'
import { usePageParamsFromRoute } from '~/scopes/cms/composables/usePageParamsFromRoute'
import { useSetCMSPageCacheTagHeader } from '~/scopes/cms/composables/useSetCMSPageCacheTagHeader'

function useFetchVotes() {
  const { pageName, pageType } = usePageParamsFromRoute()
  const $httpFetch = useHttpFetch()

  return useAsyncData(
    getAsyncDataKey('cms-page-content-article-votes', { pageType, pageName }),
    () =>
      $httpFetch('/content/page/:pageType/:pageName/votes', {
        pathParams: { pageType, pageName },
      }),
  )
}

const formatArticle = (
  response: MappedApiPageContent<ApiArticlePageContent>,
): MappedApiPageContent<ArticlePageContent> => {
  const { updateDate, displayBreadcrumb, ...res } = response

  return {
    ...res,
    updateDate: updateDate ? new Date(updateDate) : undefined,
    displayBreadcrumb:
      typeof displayBreadcrumb === 'boolean' ? displayBreadcrumb : true,
  }
}

function useFetchCMSPageContent({
  fetchFallbackOnError,
}: {
  fetchFallbackOnError: boolean
}) {
  const $httpFetch = useHttpFetch()
  const pathParams = usePageParamsFromRoute()
  const formatContentResponse = useFormatContentResponse()

  if (fetchFallbackOnError)
    return useFetchCMSPageContentWithFallback(
      '/content/page/:pageType/:pageName',
      {
        pathParams,
        transform: formatArticle,
      },
    )

  return useAsyncData(
    getAsyncDataKey('cms-page-content-article', pathParams),
    () =>
      $httpFetch('/content/page/:pageType/:pageName', {
        pathParams: {
          pageType: pathParams.pageType,
          pageName: pathParams.pageName,
        },
      }),
    {
      transform: (response: BasePageContentAPI) =>
        formatArticle(
          formatContentResponse(
            response,
          ) as MappedApiPageContent<ApiArticlePageContent>,
        ),
    },
  )
}

function useFetchRelatedArticles() {
  const $httpFetch = useHttpFetch()
  const requestURL = useRequestURL()

  return useAsyncData(
    () =>
      $httpFetch('/internal-links/v1/cards', {
        query: { source_url: requestURL.pathname },
      }),
    {
      transform: (response): ArticleCard[] => {
        const relatedArticles = response.linkLists.find(
          (l) => l.listId === 'related_articles',
        )
        if (!relatedArticles) return []

        return relatedArticles.links.map((article) => {
          return {
            title: article.title ?? '',
            category: article.tagline ?? '',
            text: article.description ?? '',
            id: article.link.href,
            imageSrc: article.image.src ?? '',
            imageAlt: article.image.alt ?? undefined,
            link: { href: article.link.relativeHref },
          }
        })
      },
      default: () => [],
    },
  )
}

export async function useFetchArticle({
  fetchFallbackOnError,
}: {
  fetchFallbackOnError: boolean
}) {
  const { setCMSCacheTagHeader } = useSetCMSPageCacheTagHeader()

  const [fetchedContent, fetchedVotes, fetchedRelatedArticles] =
    await Promise.all([
      useFetchCMSPageContent({ fetchFallbackOnError }),
      useFetchVotes(),
      useFetchRelatedArticles(),
    ])

  setCMSCacheTagHeader(fetchedContent?.data?.value)

  return {
    fetchedContent,
    fetchedVotes,
    fetchedRelatedArticles,
  }
}
